import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
    type CategoryBreakdownDto,
    type ProductsResponseDto,
    type SavingsInsightsSummaryDto,
} from 'entities/ReportSavingsSummary';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';
import {
    toSafeNumber,
    useSavingsSummaryDrillInData,
} from 'widgets/SavingsSummaryReport/model/hooks/useSavingsSummaryDrillInData';

interface UseReportsPageSavingsSummaryCategoryResult {
    isSavingsSummaryVisible: boolean | undefined;
    categoryId: string | undefined;
    categoryName: string;
    loading: boolean;
    summary: SavingsInsightsSummaryDto | null;
    byCategory: CategoryBreakdownDto[];
    products: ProductsResponseDto | null;
    totalSaved: number;
    totalSavingsPercent: number;
    orderCount: number;
    productsCount: number;
    dateFrom: Date;
    dateTo: Date;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

export const useReportsPageSavingsSummaryCategory =
    (): UseReportsPageSavingsSummaryCategoryResult => {
        const { categoryId } = useParams<{ categoryId: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();

        const locationState = location.state as {
            categoryTitle?: string;
            contextTotalSavings?: string;
        } | null;
        const categoryTitleFromState = locationState?.categoryTitle;
        const contextTotalSavings = toSafeNumber(
            locationState?.contextTotalSavings,
        );

        const {
            loading,
            summary,
            byCategory,
            products,
            totalSaved,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        } = useSavingsSummaryDrillInData({
            dimension: 'category',
            id: categoryId,
        });

        const categoryName = useMemo(() => {
            if (categoryTitleFromState) {
                return categoryTitleFromState;
            }

            const responseCategoryTitle = byCategory.find(
                (item) => item.categoryId === categoryId,
            )?.categoryTitle;

            if (responseCategoryTitle) {
                return responseCategoryTitle;
            }

            return categoryId ?? '';
        }, [byCategory, categoryId, categoryTitleFromState]);

        const totalSavingsPercent =
            contextTotalSavings > 0
                ? (totalSaved / contextTotalSavings) * 100
                : 100;

        return {
            isSavingsSummaryVisible,
            categoryId,
            categoryName,
            loading,
            summary,
            byCategory,
            products,
            totalSaved,
            totalSavingsPercent,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        };
    };
